import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useDomainServices } from "@/providers/DomainServices";
import { fileQueryKeys } from "@/shared/domain/files/hooks/fileQueryKeys";
import { sessionQueryKeys } from "@/shared/domain/sessions/hooks/sessionQueryKeys";

import { ingestionRulesQueryKeys } from "./ingestionRulesQueryKeys";
import {
  LATEST_RUNS_POLL_MS,
  latestRunsRefetchInterval,
} from "./latestRunsRefetchInterval";

/**
 * The latest ingestion run on each of `fileIds` (at most 200), keyed by file
 * id. Polls while any of them is running, and while a file in `expectRunBy`
 * has no run yet and the time given for it (ms since the epoch) has not
 * passed: a file just uploaded can be asked about before its rule's trigger
 * starts on it.
 */
export function useLatestIngestionRuns(
  fileIds: readonly string[],
  {
    enabled = true,
    orgId,
    expectRunBy = NO_EXPECTED_RUNS,
  }: {
    enabled?: boolean;
    orgId?: string;
    expectRunBy?: Readonly<Record<string, number>>;
  } = {},
) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.latestRuns(resolvedOrgId, fileIds),
    queryFn: ({ signal }) =>
      ingestionRuleService.latestRuns(fileIds, resolvedOrgId, {
        abortSignal: signal,
      }),
    enabled: enabled && fileIds.length > 0,
    staleTime: LATEST_RUNS_POLL_MS,
    refetchInterval: (query) =>
      latestRunsRefetchInterval(query.state.data, expectRunBy, Date.now()),
  });
}

const NO_EXPECTED_RUNS: Readonly<Record<string, number>> = {};

/**
 * Ingest existing files now with the rules that match them, or run a failed
 * ingestion again. Afterwards the runs, files and sessions caches refetch.
 */
export function useIngestFiles(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (fileIds: readonly string[]) =>
      ingestionRuleService.ingestFiles(fileIds, resolvedOrgId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [...ingestionRulesQueryKeys.all(resolvedOrgId), "latestRuns"],
      });
      void queryClient.invalidateQueries({ queryKey: fileQueryKeys.all() });
      void queryClient.invalidateQueries({ queryKey: sessionQueryKeys.all });
    },
  });
}

/** Files already uploaded that the rule matches and its trigger never ran on. */
export function useExistingFilesPreview(
  ruleId: string,
  { enabled = true, orgId }: { enabled?: boolean; orgId?: string } = {},
) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);

  return useQuery({
    queryKey: ingestionRulesQueryKeys.existingFiles(resolvedOrgId, ruleId),
    queryFn: ({ signal }) =>
      ingestionRuleService.previewExistingFiles(ruleId, resolvedOrgId, {
        abortSignal: signal,
      }),
    enabled,
  });
}

/** Queue ingestion of the rule's existing matching files. Admin only. */
export function useIngestExistingFiles(orgId?: string) {
  const { ingestionRuleService } = useDomainServices();
  const resolvedOrgId = useCurrentOrgId(orgId);
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleId: string) =>
      ingestionRuleService.ingestExistingFiles(ruleId, resolvedOrgId),
    onSuccess: (_preview, ruleId) => {
      void queryClient.invalidateQueries({
        queryKey: ingestionRulesQueryKeys.existingFiles(resolvedOrgId, ruleId),
      });
    },
  });
}
